import { NavLink, Navigate, Outlet } from "react-router-dom";
import { Home, CalendarCheck, TrendingUp, ShoppingBag, Menu, Dumbbell } from "lucide-react";
import { useMemberData } from "../../contexts/MemberDataContext";
import { MEMBER } from "../../constants/testIds";
import { GymLoader } from "../common/GymLoader";
import { OnboardingTourOverlay, OnboardingHelpButton } from "../onboarding/OnboardingTour";
import { useOnboardingTour } from "../onboarding/useOnboardingTour";
import { MEMBER_STEPS } from "../onboarding/onboardingSteps";

const TABS = [
  { key: "home", label: "Home", path: "/member/home", icon: Home },
  { key: "book", label: "Book", path: "/member/book", icon: CalendarCheck },
  { key: "progress", label: "Progress", path: "/member/progress", icon: TrendingUp },
  { key: "shop", label: "Shop", path: "/member/shop", icon: ShoppingBag },
  { key: "more", label: "More", path: "/member/more", icon: Menu },
];

// UX-MEM-01 §0: mobile-first — `md:max-w-md mx-auto` simulates a phone-width app even on
// desktop (design_guidelines.json). Bottom nav + sticky header both use glassmorphism (the ONE
// place this theme allows it) per the same spec.
export function MemberShell() {
  const { me, home, loading, error } = useMemberData();
  const tour = useOnboardingTour("member", me?.login_count, me?.id);

  if (loading) {
    return (
      <div className="member-app min-h-screen flex items-center justify-center" style={{ background: "#0A0A0A" }}>
        <GymLoader size={32} testId="member-shell-loading" />
      </div>
    );
  }
  if (error) return <Navigate to="/member/login" replace />;

  return (
    <div className="member-app relative min-h-screen md:max-w-md mx-auto pb-32" style={{ background: "#0A0A0A", color: "#fff" }}>
      <header className="sticky top-0 z-40 flex items-center gap-3 px-6 py-4 backdrop-blur-xl" style={{ background: "rgba(10,10,10,0.8)", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "#D6202B" }}>
          <Dumbbell size={16} color="#fff" strokeWidth={1.5} />
        </div>
        <div className="min-w-0">
          <p className="font-heading text-lg font-bold leading-none truncate">{home?.organization?.name || "FitFrikArena"}</p>
          <p className="text-xs truncate" style={{ color: "#A1A1AA" }}>Hi, {me?.name?.split(" ")[0] || "there"}</p>
        </div>
      </header>

      <main className="px-6 py-6">
        <Outlet />
      </main>

      <nav className="fixed bottom-0 left-0 right-0 md:max-w-md md:mx-auto z-50 flex items-stretch backdrop-blur-xl" style={{ background: "rgba(0,0,0,0.8)", borderTop: "1px solid rgba(255,255,255,0.1)" }}>
        {TABS.map((t) => (
          <NavLink
            key={t.key} to={t.path} data-testid={MEMBER.navTab(t.key)}
            className="flex-1 flex flex-col items-center justify-center gap-1 py-3 min-h-[56px] active:scale-95 transition-transform"
          >
            {({ isActive }) => (
              <>
                <t.icon size={20} strokeWidth={1.5} color={isActive ? "#D6202B" : "#A1A1AA"} />
                <span className="text-[11px] font-medium" style={{ color: isActive ? "#D6202B" : "#A1A1AA" }}>{t.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>
      <OnboardingTourOverlay steps={MEMBER_STEPS} tour={tour} />
      <OnboardingHelpButton onClick={tour.restart} style={{ bottom: 92 }} />
    </div>
  );
}
